import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ShoppingCart, 
  ArrowLeft, 
  CheckCircle, 
  XCircle, 
  Clock,
  Package,
  Printer,
  Download,
  Edit,
  AlertCircle,
  AlertTriangle,
  Minus,
  Plus
} from 'lucide-react';
import { usePurchaseOrders } from '../hooks/usePurchaseOrders';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import type { PurchaseOrder, PurchaseOrderStatus, PurchaseOrderItem } from '@/types/purchase-order.types';

export const PurchaseOrderDetailPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { selectedLocation } = useAuth();
  const currentLocationId = selectedLocation?.id || 'loc-1';

  const {
    orders,
    loading,
    error,
    approveOrder,
    cancelOrder,
    fetchOrder,
    updateOrder
  } = usePurchaseOrders(currentLocationId);

  const [order, setOrder] = useState<PurchaseOrder | null>(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [editableItems, setEditableItems] = useState<PurchaseOrderItem[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalItems, setOriginalItems] = useState<PurchaseOrderItem[]>([]);

  useEffect(() => {
    const loadOrder = async () => {
      if (orderId) {
        try {
          const fetchedOrder = await fetchOrder(orderId);
          if (fetchedOrder) {
            console.log('🏗️ Setting up order data:', fetchedOrder.items.map(item => ({ id: item.id, quantity: item.quantity })));
            setOrder(fetchedOrder);

            // Make deep copies to avoid reference sharing
            const editableItemsCopy = fetchedOrder.items.map(item => ({ ...item }));
            const originalItemsCopy = fetchedOrder.items.map(item => ({ ...item }));

            setEditableItems(editableItemsCopy);
            setOriginalItems(originalItemsCopy);
            console.log('🏗️ Original items set:', originalItemsCopy.map(item => ({ id: item.id, quantity: item.quantity })));
            setHasChanges(false);
          } else {
            setOrder(null);
            setEditableItems([]);
            setOriginalItems([]);
            setHasChanges(false);
          }
        } catch (error) {
          console.error('Error fetching order:', error);
          setOrder(null);
          setEditableItems([]);
          setOriginalItems([]);
          setHasChanges(false);
        }
      }
    };

    loadOrder();
  }, [orderId]);

  const getStatusBadge = (status: PurchaseOrderStatus) => {
    const variants = {
      pending: 'secondary',
      approved: 'default', 
      received: 'success',
      cancelled: 'destructive'
    } as const;
    
    const labels = {
      pending: 'Pendiente',
      approved: 'Aprobada',
      received: 'Recibida',
      cancelled: 'Cancelada'
    };
    
    const icons = {
      pending: Clock,
      approved: CheckCircle,
      received: Package,
      cancelled: XCircle
    };
    
    const Icon = icons[status];
    
    return (
      <Badge variant={variants[status]} className="flex items-center gap-2 text-sm px-3 py-1">
        <Icon className="w-4 h-4" />
        {labels[status]}
      </Badge>
    );
  };

  const formatCurrency = (amount: number | undefined | null) => {
    const numericAmount = Number(amount) || 0;
    if (isNaN(numericAmount)) {
      return 'S/ 0.00';
    }
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(numericAmount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-PE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getLocationName = (locationId: string) => {
    const locations: { [key: string]: string } = {
      'loc-1': 'Sede Principal',
      'loc-2': 'Sucursal Norte',
      'loc-3': 'Sucursal Sur'
    };
    return locations[locationId] || 'Desconocida';
  };

  const getDiscrepancy = (item: any) => {
    if (item.receivedQuantity === undefined || item.receivedQuantity === null) {
      return null; // No discrepancy data available
    }
    return item.receivedQuantity - item.quantity;
  };

  const getDiscrepancyIcon = (discrepancy: number | null) => {
    if (discrepancy === null || discrepancy === 0) return null;
    if (discrepancy > 0) {
      return <Plus className="w-4 h-4 text-green-600" />;
    }
    return <Minus className="w-4 h-4 text-red-600" />;
  };

  const getDiscrepancyBadge = (discrepancy: number | null) => {
    if (discrepancy === null) {
      return (
        <Badge variant="secondary" className="bg-gray-100 text-gray-800">
          N/A
        </Badge>
      );
    } else if (discrepancy === 0) {
      return (
        <Badge variant="secondary" className="bg-green-100 text-green-800">
          OK
        </Badge>
      );
    } else if (discrepancy > 0) {
      return (
        <Badge variant="secondary" className="bg-blue-100 text-blue-800">
          +{discrepancy}
        </Badge>
      );
    } else {
      return (
        <Badge variant="secondary" className="bg-red-100 text-red-800">
          {discrepancy}
        </Badge>
      );
    }
  };

  const updateItemQuantity = (itemIndex: number, newQuantity: number) => {
    const updatedItems = [...editableItems];
    const item = updatedItems[itemIndex];

    // Ensure numeric values
    const quantity = Math.max(0, Number(newQuantity) || 0);
    const unitCost = Number(item.unitCost) || 0;

    // Update quantity and recalculate total cost
    item.quantity = quantity;
    item.totalCost = quantity * unitCost;

    setEditableItems(updatedItems);

    // Check if there are changes compared to original (using rounded comparison for floating point precision)
    const hasChanges = updatedItems.some((item, index) => {
      const currentQty = Number(item.quantity) || 0;
      const originalQty = Number(originalItems[index]?.quantity) || 0;
      const difference = Math.abs(currentQty - originalQty);
      console.log(`🔍 Item ${index}: current=${currentQty}, original=${originalQty}, diff=${difference}, hasChange=${difference > 0.001}`);
      return difference > 0.001; // Allow for floating point precision
    });
    console.log(`🔍 Total hasChanges: ${hasChanges}`);
    setHasChanges(hasChanges);

    // Update order totals (but NOT the items - keep original items intact)
    if (order) {
      const newSubtotal = updatedItems.reduce((sum, item) => {
        const itemTotal = Number(item.totalCost) || 0;
        return sum + itemTotal;
      }, 0);
      const newTax = newSubtotal * 0.18;
      const newTotal = newSubtotal + newTax;

      setOrder({
        ...order,
        // DON'T update items here - keep original items
        subtotal: Number(newSubtotal.toFixed(2)),
        tax: Number(newTax.toFixed(2)),
        total: Number(newTotal.toFixed(2))
      });
    }
  };

  const handleApproveClick = () => {
    setShowApproveModal(true);
  };

  const handleCancelClick = () => {
    setShowCancelModal(true);
  };

  const handleApproveConfirm = async () => {
    if (order) {
      setIsProcessing(true);
      try {
        // First, save any pending changes if they exist
        if (hasChanges) {
          const updateData = {
            items: editableItems.map(item => ({
              id: item.id,
              quantity: item.quantity,
              unitCost: item.unitCost,
              totalCost: item.totalCost
            })),
            subtotal: order.subtotal,
            tax: order.tax,
            total: order.total
          };

          const updatedOrder = await updateOrder(order.id, updateData);
          if (!updatedOrder) {
            toast.error('Error al guardar los cambios antes de aprobar');
            setIsProcessing(false);
            return;
          }

          // Update local state with saved changes
          setOrder(updatedOrder);
          setEditableItems(updatedOrder.items.map(item => ({ ...item })));
          setOriginalItems(updatedOrder.items.map(item => ({ ...item })));
          setHasChanges(false);
        }

        // Then approve the order
        const success = await approveOrder(order.id, 'Aprobada desde página de detalles');
        if (success) {
          setShowApproveModal(false);
          toast.success('Orden de compra aprobada exitosamente');
          // Reload the order to get updated data
          const updatedOrder = await fetchOrder(order.id);
          if (updatedOrder) {
            setOrder(updatedOrder);
            setEditableItems(updatedOrder.items.map(item => ({ ...item })));
            setOriginalItems(updatedOrder.items.map(item => ({ ...item })));
            setHasChanges(false);
          }
        } else {
          toast.error('Error al aprobar la orden de compra');
        }
      } catch (error) {
        console.error('Error approving order:', error);
        toast.error('Error al procesar la aprobación');
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleCancelConfirm = async () => {
    if (order) {
      setIsProcessing(true);
      try {
        const success = await cancelOrder(order.id, 'Cancelada desde página de detalles');
        if (success) {
          setShowCancelModal(false);
          toast.success('Orden de compra cancelada exitosamente');
          // Reload the order to get updated data
          const updatedOrder = await fetchOrder(order.id);
          if (updatedOrder) {
            setOrder(updatedOrder);
            setEditableItems(updatedOrder.items.map(item => ({ ...item })));
            setOriginalItems(updatedOrder.items.map(item => ({ ...item })));
            setHasChanges(false);
          }
        } else {
          toast.error('Error al cancelar la orden de compra');
        }
      } catch (error) {
        console.error('Error cancelling order:', error);
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleReceive = () => {
    if (order) {
      navigate(`/purchase-orders/${order.id}/receive`);
    }
  };

  const handleSaveChanges = async () => {
    if (!order || !hasChanges) return;

    setIsProcessing(true);
    try {
      // Prepare the data to send
      const updateData = {
        items: editableItems.map(item => ({
          id: item.id,
          quantity: item.quantity,
          unitCost: item.unitCost,
          totalCost: item.totalCost
        })),
        subtotal: order.subtotal,
        tax: order.tax,
        total: order.total
      };

      const updatedOrder = await updateOrder(order.id, updateData);

      if (updatedOrder) {
        setOrder(updatedOrder);
        setEditableItems(updatedOrder.items.map(item => ({ ...item })));
        setOriginalItems(updatedOrder.items.map(item => ({ ...item })));
        setHasChanges(false);
        toast.success('Cambios guardados exitosamente');
      } else {
        toast.error('Error al guardar los cambios');
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      toast.error('Error al guardar los cambios');
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <ShoppingCart className="w-16 h-16 text-gray-400 mx-auto mb-4 animate-pulse" />
          <h2 className="text-xl font-semibold text-gray-700">Cargando orden de compra...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Error al cargar orden</h2>
          <p className="text-red-600">{error}</p>
          <Button 
            onClick={() => navigate('/purchase-orders')} 
            className="mt-4"
            variant="outline"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a Órdenes
          </Button>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <ShoppingCart className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Orden no encontrada</h2>
          <p className="text-gray-600">La orden de compra solicitada no existe o no tienes permisos para verla.</p>
          <Button 
            onClick={() => navigate('/purchase-orders')} 
            className="mt-4"
            variant="outline"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a Órdenes
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 md:p-6 space-y-6 h-full overflow-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/purchase-orders')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{order.orderNumber}</h1>
              {getStatusBadge(order.status)}
            </div>
            <p className="text-gray-600 text-sm md:text-base">
              Orden de compra - {order.supplierName}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Exportar
          </Button>
        </div>
      </div>

      {/* Top Section - Information, Actions, and Financial Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Order Details */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-macartur-primary" />
              Información General
            </CardTitle>
          </CardHeader>
          <CardContent className="py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
              <div>
                <span className="text-gray-600">Fecha de Orden:</span>
                <div className="font-semibold mt-1">{formatDate(order.createdAt)}</div>
              </div>
              
              <div>
                <span className="text-gray-600">Sucursal:</span>
                <div className="font-semibold mt-1">{getLocationName(order.locationId)}</div>
              </div>
              
              <div>
                <span className="text-gray-600">Solicitado por:</span>
                <div className="font-semibold mt-1">
                  {order.requester ? `${order.requester.firstName} ${order.requester.lastName}` : order.requestedBy}
                </div>
              </div>

              {/* Show approval fields for approved and received orders */}
              {(order.status === 'approved' || order.status === 'received') && order.approvedBy && (
                <div>
                  <span className="text-gray-600">Aprobado por:</span>
                  <div className="font-semibold mt-1">{order.approvedBy}</div>
                </div>
              )}
              
              {(order.status === 'approved' || order.status === 'received') && order.approvedAt && (
                <div>
                  <span className="text-gray-600">Fecha de Aprobación:</span>
                  <div className="font-semibold mt-1">{formatDate(order.approvedAt)}</div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Financial Summary */}
        <Card>
          <CardHeader>
            <CardTitle>Resumen Financiero</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-semibold">{formatCurrency(order.subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">IGV (18%):</span>
                <span className="font-semibold">{formatCurrency(order.tax)}</span>
              </div>
              <div className="border-t pt-2 mt-2" />
              <div className="flex justify-between text-lg">
                <span className="font-semibold">Total Original:</span>
                <span className="font-bold text-gray-900">{formatCurrency(order.total)}</span>
              </div>

              {order.status === 'received' && (
                <>
                  <div className="border-t pt-2 mt-2" />
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal Recibido:</span>
                    <span className="font-semibold">{formatCurrency(order.receivedSubtotal || order.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-lg">
                    <span className="font-semibold">Total Recibido:</span>
                    <span className="font-bold text-blue-600">{formatCurrency(order.receivedTotal || order.total)}</span>
                  </div>

                  {/* Solo mostrar diferencia si hay discrepancia */}
                  {(order.discrepancyAmount !== undefined && order.discrepancyAmount !== null && order.discrepancyAmount !== 0) && (
                    <div className="flex justify-between text-lg">
                      <span className="font-semibold">Diferencia:</span>
                      <span className={`font-bold ${
                        order.discrepancyAmount > 0 ? 'text-red-600' :
                        order.discrepancyAmount < 0 ? 'text-green-600' :
                        'text-gray-900'
                      }`}>
                        {formatCurrency(order.discrepancyAmount)}
                      </span>
                    </div>
                  )}

                  {/* Mensaje cuando no hay discrepancia */}
                  {(order.discrepancyAmount === undefined || order.discrepancyAmount === null || order.discrepancyAmount === 0) && (
                    <div className="flex justify-between text-lg">
                      <span className="font-semibold">Estado:</span>
                      <span className="font-bold text-green-600">Sin discrepancia</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Actions Section */}
        {(order.status === 'pending' || order.status === 'approved' || order.status === 'cancelled') && (
          <Card>
            <CardHeader>
              <CardTitle>Acciones</CardTitle>
            </CardHeader>
            <CardContent>
              {order.status === 'cancelled' ? (
                <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-red-900 mb-1">Orden Cancelada</h4>
                    <p className="text-sm text-red-700">
                      No se puede editar ni procesar.
                    </p>
                    {order.cancelledBy && (
                      <p className="text-xs text-red-600 mt-2">
                        Cancelada por: <span className="font-medium">{order.cancelledBy}</span>
                        {order.cancelledAt && (
                          <span> • {formatDate(order.cancelledAt)}</span>
                        )}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {order.status === 'pending' && hasChanges && (
                    <Button
                      onClick={handleSaveChanges}
                      disabled={isProcessing}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      <Package className="w-4 h-4 mr-2" />
                      {isProcessing ? 'Guardando...' : 'Guardar Cambios'}
                    </Button>
                  )}
                  
                  {order.status === 'pending' && (
                    <>
                      <Button
                        onClick={() => {
                          console.log(`🔘 Button clicked - hasChanges: ${hasChanges}`);
                          handleApproveClick();
                        }}
                        disabled={isProcessing}
                        className="w-full bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        {(() => {
                          const buttonText = hasChanges ? 'Guardar y Aprobar Orden' : 'Aprobar Orden';
                          console.log(`🔘 Button render - hasChanges: ${hasChanges}, text: "${buttonText}"`);
                          return buttonText;
                        })()}
                      </Button>
                      <Button
                        onClick={handleCancelClick}
                        disabled={isProcessing}
                        variant="outline"
                        className="w-full text-red-600 hover:text-red-700"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Cancelar Orden
                      </Button>
                    </>
                  )}
                  
                  {order.status === 'approved' && (
                    <Button
                      onClick={handleReceive}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      <Package className="w-4 h-4 mr-2" />
                      Recibir Mercadería
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Reception Section - Only for received orders */}
        {order.status === 'received' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5 text-green-600" />
                Información de Recepción
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                {order.status === 'received' && order.receiver && (
                  <div>
                    <span className="text-gray-600">Recibido por:</span>
                    <div className="font-semibold mt-1">
                      {order.receiver ? `${order.receiver.firstName} ${order.receiver.lastName}` : order.receivedBy}
                    </div>
                  </div>
                )}
                
                {order.receivedAt && (
                  <div>
                    <span className="text-gray-600">Fecha de Recepción:</span>
                    <div className="font-semibold mt-1">{formatDate(order.receivedAt)}</div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Items Section - Full Width */}
      <Card>
        <CardHeader>
          <CardTitle>Items Solicitados ({editableItems.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Insumo
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cantidad
                  </th>
                  {order.status === 'received' && (
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cantidad Recibida
                    </th>
                  )}
                  {order.status !== 'received' && (
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Unidad
                    </th>
                  )}
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Precio Unit.
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subtotal
                  </th>
                  {order.status === 'received' && (
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Discrepancia
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {editableItems.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <div>
                          <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                            {item.ingredientName}
                            {order.status === 'received' && item.receivedQuantity !== undefined && (
                              getDiscrepancyIcon(getDiscrepancy(item))
                            )}
                          </div>
                          {item.specifications && (
                            <div className="text-xs text-gray-500 mt-1">
                              {item.specifications}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      {order?.status === 'pending' ? (
                        <Input
                          type="number"
                          value={item.quantity || 0}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Handle empty string and parse as number
                            const numericValue = value === '' ? 0 : Number(value);
                            updateItemQuantity(index, isNaN(numericValue) ? 0 : numericValue);
                          }}
                          min={0}
                          step={0.01}
                          className="w-24 text-center mx-auto"
                        />
                      ) : (
                        <div className="text-sm font-medium text-gray-900">
                          {item.quantity} {item.unit}
                        </div>
                      )}
                    </td>
                    {order.status === 'received' && (
                      <td className="px-4 py-4 text-center">
                        <div className="text-sm font-medium text-gray-900">
                          {item.receivedQuantity || 0} {item.unit}
                        </div>
                      </td>
                    )}
                    {order.status !== 'received' && (
                      <td className="px-4 py-4 text-center">
                        <div className="text-sm text-gray-900">
                          {item.unit}
                        </div>
                      </td>
                    )}
                    <td className="px-4 py-4 text-center">
                      <div className="text-sm text-gray-900">
                        {formatCurrency(item.unitCost)}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(item.totalCost)}
                      </div>
                    </td>
                    {order.status === 'received' && (
                      <td className="px-4 py-4 text-center">
                        {getDiscrepancyBadge(getDiscrepancy(item))}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Notes */}
      {order.notes && (
        <Card>
          <CardHeader>
            <CardTitle>Notas y Observaciones</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">{order.notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Approve Confirmation Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Aprobar Orden de Compra</h3>
                  <p className="text-sm text-gray-600">¿Está seguro de aprobar esta orden?</p>
                </div>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Orden:</span> {order?.orderNumber}
                </p>
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Proveedor:</span> {order?.supplierName}
                </p>
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Total:</span> {order && formatCurrency(order.total)}
                </p>
              </div>

              <p className="text-sm text-gray-600 mb-6">
                Esta acción aprobará la orden y la marcará como lista para recibir mercadería.
              </p>

              <div className="flex gap-3">
                <Button
                  onClick={() => setShowApproveModal(false)}
                  variant="outline"
                  className="flex-1"
                  disabled={isProcessing}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleApproveConfirm}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Procesando...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Aprobar Orden
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Cancelar Orden de Compra</h3>
                  <p className="text-sm text-gray-600">¿Está seguro de cancelar esta orden?</p>
                </div>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Orden:</span> {order?.orderNumber}
                </p>
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Proveedor:</span> {order?.supplierName}
                </p>
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Total:</span> {order && formatCurrency(order.total)}
                </p>
              </div>

              <div className="bg-red-50 p-4 rounded-lg mb-6">
                <p className="text-sm text-red-800 font-medium mb-1">⚠️ Acción irreversible</p>
                <p className="text-sm text-red-700">
                  Una vez cancelada, la orden no podrá ser aprobada ni recibida.
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => setShowCancelModal(false)}
                  variant="outline"
                  className="flex-1"
                  disabled={isProcessing}
                >
                  Mantener Orden
                </Button>
                <Button
                  onClick={handleCancelConfirm}
                  variant="destructive"
                  className="flex-1"
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Procesando...
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4 mr-2" />
                      Cancelar Orden
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};